// =============================================================
//  Icons — Lucide, rendered through the existing markup hook.
//
//  The views and page scripts write <i class="fas fa-<name>"> (the
//  FontAwesome convention the app grew up with). This module swaps each
//  of those for an inline Lucide SVG, the same way FontAwesome's i2svg
//  did, so no view or script had to change:
//    - initial pass on load
//    - MutationObserver for icons added later (modals filled by JS,
//      Turbo body swaps, fetch fragments)
//    - a class change on a rendered icon (e.g. toggling fa-chevron-right
//      ↔ fa-chevron-down) re-renders it
//  Sizes/modifiers (fa-2x, fa-fw, fa-spin, …) are plain CSS in
//  _components.scss. New icon: add a line to MAP below; an unmapped name
//  renders as a circle and logs a warning in development.
// =============================================================

import {
  createElement, Circle,
  Info, Plus, TriangleAlert, ArrowDown, Save, X, Check, Users, CircleCheck, Hourglass, Trash2, CircleX,
  Download, Building2, ArrowRight, ArrowLeftRight, CalendarRange, ArrowLeft, Star, Shield, Lock, Undo2,
  Table, SquarePen, TreePalm, Send, Headset, Clock, ChevronDown, CalendarDays, CalendarCheck, KeyRound,
  Mail, ChartBar, Calendar, Megaphone, FileOutput, ChevronRight, CalendarMinus, Bell, BellOff, ChevronUp,
  SlidersHorizontal, History, Database, CalendarX, UserPlus, UserCog, CalendarClock, Tags, Gauge,
  LoaderCircle, Search, Rocket, Percent, ClipboardPaste, IdCard, Hand, FileInput, FileSpreadsheet, Eye,
  Settings, CalendarPlus, Target, Zap, Activity, UserPen, UserCheck, User, Upload, RefreshCw, Share, Plug,
  Moon, List, Layers, GripVertical, Filter, Receipt, EyeOff, Copy, ChevronLeft, ChartLine, ChevronsRight,
  ChevronsLeft, UserX, LayoutGrid, Sun, LogOut, Ruler, PhoneCall, Pen, Pause, Palette, MousePointer,
  WandSparkles, Globe, FastForward, FlaskConical, Flag, FileUp, FileSignature, FileText, File, CircleAlert,
  Eraser, Ellipsis, Monitor, Settings2, Presentation, Book, Menu, ChevronsDown, Contrast, Inbox, ExternalLink,
  Smartphone
} from 'lucide'

// Ook een kale `.fa` (FA4-markup) renderen: die viel anders stil weg —
// dat was de bug met de lege verplaatsknoppen op Kalender en Units.
const ICON_SELECTOR = 'i.fas, i.far, i.fab, i.fal, i.fa'

const MAP = {
  'info-circle': Info, plus: Plus, 'exclamation-triangle': TriangleAlert, 'arrow-down': ArrowDown, save: Save,
  times: X, check: Check, users: Users, 'check-circle': CircleCheck, 'hourglass-half': Hourglass, trash: Trash2,
  'trash-alt': Trash2, 'times-circle': CircleX, download: Download, building: Building2, 'arrow-right': ArrowRight,
  'exchange-alt': ArrowLeftRight, 'calendar-week': CalendarRange, 'arrow-left': ArrowLeft, star: Star,
  'shield-alt': Shield, lock: Lock, undo: Undo2, table: Table, edit: SquarePen, 'umbrella-beach': TreePalm,
  'paper-plane': Send, headset: Headset, clock: Clock, 'chevron-down': ChevronDown, 'calendar-day': CalendarDays,
  'calendar-check': CalendarCheck, key: KeyRound, envelope: Mail, 'chart-bar': ChartBar, 'calendar-alt': Calendar,
  bullhorn: Megaphone, 'file-export': FileOutput, 'chevron-right': ChevronRight, 'calendar-minus': CalendarMinus,
  bell: Bell, nudge: Bell, 'bell-slash': BellOff, 'angle-up': ChevronUp, 'sliders-h': SlidersHorizontal, history: History, database: Database,
  'calendar-times': CalendarX, 'user-plus': UserPlus, 'user-friends': Users, 'user-cog': UserCog,
  'user-clock': CalendarClock, tags: Tags, 'tachometer-alt': Gauge, spinner: LoaderCircle, search: Search,
  rocket: Rocket, percentage: Percent, paste: ClipboardPaste, 'id-card': IdCard, 'hand-paper': Hand,
  'file-import': FileInput, 'file-excel': FileSpreadsheet, 'file-csv': FileSpreadsheet, eye: Eye, cog: Settings,
  'calendar-plus': CalendarPlus, bullseye: Target, bolt: Zap, 'wave-square': Activity, 'user-edit': UserPen,
  'user-check': UserCheck, user: User, 'user-circle': User, upload: Upload, 'sync-alt': RefreshCw, sync: RefreshCw,
  'share-square': Share, plug: Plug, moon: Moon, list: List, 'list-ul': List, 'layer-group': Layers,
  'grip-vertical': GripVertical, filter: Filter, 'file-invoice-dollar': Receipt, 'eye-slash': EyeOff, copy: Copy,
  'chevron-left': ChevronLeft, 'chart-line': ChartLine, 'angle-double-right': ChevronsRight,
  'angle-double-left': ChevronsLeft, 'angle-double-down': ChevronsDown, 'user-slash': UserX, 'th-large': LayoutGrid,
  sun: Sun, 'sign-out-alt': LogOut, 'ruler-horizontal': Ruler, 'phone-volume': PhoneCall, pen: Pen, pause: Pause,
  palette: Palette, 'mouse-pointer': MousePointer, magic: WandSparkles, 'globe-europe': Globe, forward: FastForward,
  flask: FlaskConical, flag: Flag, 'file-upload': FileUp, 'file-signature': FileSignature, 'file-pdf': FileText,
  file: File, 'exclamation-circle': CircleAlert, eraser: Eraser, 'ellipsis-h': Ellipsis, desktop: Monitor, 'mobile-alt': Smartphone,
  cogs: Settings2, 'chalkboard-teacher': Presentation, book: Book, bars: Menu, adjust: Contrast, inbox: Inbox,
  'external-link-alt': ExternalLink
}

const MODIFIERS = new Set(['fa-fw', 'fa-xs', 'fa-sm', 'fa-lg', 'fa-2x', 'fa-3x', 'fa-4x', 'fa-5x', 'fa-spin', 'fa-pulse'])
const SVG_NS = 'http://www.w3.org/2000/svg'
const warned = new Set()

function nameOf (el) {
  for (const c of el.classList) {
    if (c.startsWith('fa-') && !MODIFIERS.has(c)) {
      const n = c.slice(3)
      if (MAP[n]) return n
    }
  }
  return null
}

function build (name, classes) {
  const node = MAP[name] || Circle
  if (!MAP[name] && name !== null && !warned.has(name)) {
    warned.add(name)
    if (import.meta.env && import.meta.env.DEV) console.warn('[icons] unmapped icon fa-' + name)
  }
  return createElement(node, { class: 'ui-icon ' + classes, 'aria-hidden': 'true', focusable: 'false', 'stroke-width': '2' })
}

function firstFaName (el) {
  for (const c of el.classList) if (c.startsWith('fa-') && !MODIFIERS.has(c)) return c.slice(3)
  return null
}

// <i class="fas fa-x"> → <svg …>, keeping id/style/title/data-*/aria-*.
function render (i) {
  const name = nameOf(i) || firstFaName(i)
  const svg = build(name, i.className)
  for (const a of Array.from(i.attributes)) {
    if (a.name === 'class') continue
    svg.setAttribute(a.name, a.value)
  }
  if (i.getAttribute('title')) {
    const t = document.createElementNS(SVG_NS, 'title')
    t.textContent = i.getAttribute('title')
    svg.insertBefore(t, svg.firstChild)
    if (!svg.hasAttribute('aria-label')) svg.setAttribute('aria-hidden', 'false')
  }
  svg.dataset.icon = name || ''
  i.replaceWith(svg)
}

// A rendered icon whose fa-* class changed (toggleClass on a caret) → swap
// the paths for the new icon; the element identity stays.
function refresh (svg) {
  const name = nameOf(svg) || firstFaName(svg)
  if (!name || name === svg.dataset.icon) return
  const fresh = build(name, svg.getAttribute('class'))
  Array.from(svg.children).forEach((c) => { if (c.tagName !== 'title') c.remove() })
  Array.from(fresh.children).forEach((c) => svg.appendChild(c))
  svg.dataset.icon = name
}

function scan (root) {
  if (!root || !root.querySelectorAll) return
  // `i.fa` staat er als vangnet bij: oude markup met het kale FA4-prefix
  // rendert anders NIETS (lege knop) in plaats van een icoon — dat was de
  // bug met de verplaatsknoppen op Kalender en Units.
  if (root.matches && root.matches(ICON_SELECTOR)) render(root)
  root.querySelectorAll(ICON_SELECTOR).forEach(render)
}

const observer = new MutationObserver((records) => {
  for (const r of records) {
    if (r.type === 'childList') r.addedNodes.forEach((n) => { if (n.nodeType === 1) scan(n) })
    else if (r.type === 'attributes' && r.target.tagName === 'svg' && r.target.classList.contains('ui-icon')) refresh(r.target)
  }
})

function start () {
  scan(document.body)
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start)
else start()
